/**
 * Recipes MV2 query API — isolated from MV1 pages/Baking.jsx.
 * Per-endpoint fetchers (MV1-style resilience).
 */
import api, { getRecipes, getDryCakes, getCheesecakes } from "@/lib/api";
import { buildIngredientCatalog } from "@/lib/recipeIngredientCost";

export const RECIPES_QUERY_KEY = "recipes";
export const DRY_CAKES_QUERY_KEY = "dry-cakes";
export const CHEESECAKES_QUERY_KEY = "cheesecakes";
export const RTG_STOCK_QUERY_KEY = "rtg-stock";
export const RTG_STOCK_LEVELS_QUERY_KEY = "rtg-stock-levels";
export const RTG_STOCK_HISTORY_QUERY_KEY = "rtg-stock-history";
export const INGREDIENT_CATALOG_QUERY_KEY = "ingredient-catalog";

function isAbortError(error, signal) {
  if (signal?.aborted) return true;
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

async function safeSectionFetch(label, signal, fetcher, fallback) {
  try {
    const data = await fetcher();
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    return { data, error: null };
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    console.error(`Recipes MV2: failed to fetch ${label}:`, error);
    return { data: fallback, error };
  }
}

export async function fetchRecipesFromApi(signal) {
  const response = await getRecipes();
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return response.data.recipes || [];
}

export async function fetchDryCakesFromApi(signal) {
  const response = await getDryCakes();
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return response.data.dry_cakes || [];
}

export async function fetchCheesecakesFromApi(signal) {
  const response = await getCheesecakes();
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return response.data.cheesecakes || [];
}

export async function fetchRtgStockLevelsFromApi(signal) {
  const { data } = await safeSectionFetch(
    "rtg-stock",
    signal,
    () => api.get("/rtg-stock", { signal }).then((r) => r.data),
    null
  );
  return data;
}

export async function fetchRtgStockHistoryFromApi(signal) {
  const { data } = await safeSectionFetch(
    "rtg-stock-history",
    signal,
    () => api.get("/rtg-stock/history?limit=20", { signal }).then((r) => r.data.entries || []),
    []
  );
  return data;
}

/** Composed fetch — safe section fetchers; kept for guard wiring. */
export async function fetchRtgStockFromApi(signal) {
  const [rtgStock, rtgHistory] = await Promise.all([
    fetchRtgStockLevelsFromApi(signal),
    fetchRtgStockHistoryFromApi(signal),
  ]);
  return { rtgStock, rtgHistory };
}

export async function fetchIngredientCatalogFromApi(signal) {
  const res = await api.get("/ingredient-prices", { signal });
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  const priceList = res.data.prices || res.data || [];
  const catalog = buildIngredientCatalog(priceList);
  const names = Object.values(catalog).map((e) => e.name);
  return {
    ingredientCatalog: catalog,
    ingredientNames: [...new Set(names)].sort((a, b) => a.localeCompare(b)),
  };
}
